import React, { useState, useEffect } from "react";
import axios from "axios";
import WorkflowDetails from "./WorkflowDetails";
import ModifyWorkflow from "./ModifyWorkflow";
import LaunchBuild from "./LaunchBuild";
import BYOPWorkflow from "./BYOPWorkflow";
import BYOPEdit from "./BYOPEdit";
import API_BASE from "./config";
import "./css/AllChats.css";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";

const PAGE_SIZE = 10;

// Build a compact, windowed list of page numbers (with "..." gaps) so the
// pager never overflows the 300px sidebar even with many pages.
const getPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const wanted = [1, total, current, current - 1, current + 1].filter(
    (p) => p >= 1 && p <= total
  );
  const uniqueSorted = [...new Set(wanted)].sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of uniqueSorted) {
    if (p - prev > 1) out.push("...");
    out.push(p);
    prev = p;
  }
  return out;
};

const AllChats = () => {
const [chats, setChats] = useState([]);
const [selectedChatId, setSelectedChatId] = useState(null);
const [conversation, setConversation] = useState([]);
const [workflowStatus, setWorkflowStatus] = useState("");
const [workflowId, setWorkflowId] = useState(null);
const [cognitoUserId, setCognitoUserId] = useState(null);
const [userId, setUserId] = useState(null);
const [workflowMetadataId, setWorkflowMetadataId] = useState(null);
const [organization, setOrganization] = useState(null);
const [dropdownOpen, setDropdownOpen] = useState(null);
const [renameInput, setRenameInput] = useState("");
const [viewMode, setViewMode] = useState("VIEW");
const [shareModalOpen, setShareModalOpen] = useState(false);
const [shareTargetUser, setShareTargetUser] = useState(null);
const [orgUsers, setOrgUsers] = useState([]);
const [shareStep, setShareStep] = useState("select"); // "select" | "confirm" | "success"
const [shareChatId, setShareChatId] = useState(null);
const [currentPage, setCurrentPage] = useState(1);

useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest(".chat-dropdown")) {
      setDropdownOpen(null);
      setRenameInput("");
    }
  };
  document.addEventListener("click", handleClickOutside);
  return () => document.removeEventListener("click", handleClickOutside);
}, []);

useEffect(() => {
  const fetchSession = async () => {
    try {
      const response = await fetch("/api/auth/session", {
        method: "GET",
        credentials: "include"
      });
      const data = await response.json();
      
      if (!data.authenticated) {
        console.error("User not authenticated");
        return;
      }

      setCognitoUserId(data.cognitoUserId);
      setUserId(data.userId);
      setOrganization(data.organization);
    } catch (error) {
      console.error("Error fetching session:", error);
    }
  };

  fetchSession();
}, []);


const fetchAllChats = async () => {
  try {
    if (!cognitoUserId) return;

    const result = await axios.get(`${API_BASE}/all-chats`, {
      params: { user_id: userId }
    });

    const sorted = result.data.sort((a, b) => {
      if (a.pinned === "Y" && b.pinned !== "Y") return -1;
      if (a.pinned !== "Y" && b.pinned === "Y") return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    setChats(sorted);
  } catch (error) {
    console.error("Error fetching all chats:", error);
  }
};

useEffect(() => {
  fetchAllChats();
}, [cognitoUserId]);

// A BYOP (bring-your-own) public pipeline is detached from workflow_metadata
// and rendered in its own read-only view instead of WorkflowDetails.
const isByop = (chat) => chat?.pipeline_type === "byop";

const handleChatSelection = async (chatId, mode = "VIEW") => {
  const chat = chats.find((c) => c.id === chatId);
  if (isByop(chat)) {
    // No workflow_metadata / chat-history for BYOP pipelines — show the BYOP view
    // (launch page by default, or the edit module when mode === "BYOP_EDIT").
    setSelectedChatId(chatId);
    setViewMode(mode === "BYOP_EDIT" ? "BYOP_EDIT" : "BYOP");
    setWorkflowStatus("");
    setWorkflowMetadataId(null);
    setConversation([]);
    return;
  }

  try {
    const result = await axios.get(`${API_BASE}/workflow-metadata/${chatId}`);
    if (result.data?.length > 0) {
      setWorkflowStatus("COMPLETE");
      setWorkflowId(chatId);
      setWorkflowMetadataId(result.data[0].id);
    } else {
      setWorkflowStatus("INCOMPLETE");
    }

    const historyResult = await axios.get(`${API_BASE}/chat-history/${chatId}`);
    const history = historyResult.data.history.map((item) => ({
      role: item.role,
      content: item.message
    }));

    setConversation(history);
    setSelectedChatId(chatId);
    setViewMode(mode);
  } catch (error) {
    console.error("Error fetching chat details:", error);
  }
};

const handleRename = async (chatId) => {
  const valid = /^[\w\s]+$/.test(renameInput);
  if (!valid) return alert("Only letters, numbers, spaces, and underscores allowed.");
  await axios.post(`${API_BASE}/rename-chat`, { chatId, newName: renameInput });
  setRenameInput("");
  setDropdownOpen(null);
  fetchAllChats();
};

const handleDelete = async (chatId) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this build?");
  if (!confirmDelete) return;
  await axios.post(`${API_BASE}/delete-chat`, { chatId });
  fetchAllChats();
  if (selectedChatId === chatId) {
    setSelectedChatId(null);
    setConversation([]);
  }
};

const handleShare = async (chatId) => {
  setShareChatId(chatId);
  setShareStep("select");
  setShareTargetUser(null);
  setShareModalOpen(true);

  // Fetch all enabled users in the organization
  const result = await axios.get(`${API_BASE}/org-enabled-users`, {
    params: { organization }
  });

  setOrgUsers(result.data); // [{ id, name, cognito_user_id }]
};

const handleConfirmShare = async () => {
  try {
    const res = await axios.post(`${API_BASE}/share-workflow`, {
      chatId: shareChatId,
      targetCognitoUserId: shareTargetUser
    });

    setShareStep("success");
  } catch (err) {
    console.error("Share failed:", err);
    alert("Failed to share workflow");
  }
};

const selectedChat = chats.find((c) => c.id === selectedChatId);
const selectedValidated = selectedChat?.validated_final === "Y";

// Client-side pagination: show at most PAGE_SIZE builds at a time.
const totalPages = Math.max(1, Math.ceil(chats.length / PAGE_SIZE));
const pagedChats = chats.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

// Keep the current page valid as the list shrinks (e.g. after a delete).
useEffect(() => {
  if (currentPage > totalPages) setCurrentPage(totalPages);
}, [currentPage, totalPages]);

return (
  <div className="allchats-container">
    <div className="allchats-sidebar">
      <style>{`
        .cg-spinner {
          display: inline-block;
          width: 12px;
          height: 12px;
          border: 2px solid #d1a054;
          border-top-color: transparent;
          border-radius: 50%;
          animation: cg-spin 0.7s linear infinite;
          vertical-align: middle;
        }
        @keyframes cg-spin { to { transform: rotate(360deg); } }
      `}</style>
      <h4>All Builds</h4>

      <div className="chat-list">
      {chats.length > 0 ? (
        pagedChats.map((chat) => (
          <div
            key={chat.id}
            className={`chat-item ${selectedChatId === chat.id ? "selected" : ""}`}
          >
            <div
              className="chat-main"
              onClick={() => handleChatSelection(chat.id)}
            >
              <div>
                {chat.pinned === "Y" && <span className="pin-icon">📌</span>}
                {chat.short_name || `Build #${chat.id}`}
                {isByop(chat) ? (
                  chat.validated_final === "Y" ? (
                    <div style={{ fontSize: "11px", marginTop: "3px", fontStyle: "italic", color: "#0369a1" }}>
                      🌐 BYOP Imported
                    </div>
                  ) : chat.validation_status === "failed" ? (
                    <div style={{ fontSize: "11px", marginTop: "2px", fontStyle: "italic", color: "#b91c1c" }}>
                      ⚠️ Docker validation failed
                    </div>
                  ) : (
                    <div style={{ fontSize: "11px", marginTop: "3px", fontStyle: "italic", color: "#b45309", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span className="cg-spinner" /> Validating container…
                    </div>
                  )
                ) : (
                  chat.validated_final !== "Y" &&
                  (chat.validation_status === "failed" ? (
                    <div style={{ fontSize: "11px", marginTop: "2px", fontStyle: "italic", color: "#b91c1c" }}>
                      ⚠️ Validation failed
                    </div>
                  ) : (
                    <div style={{ fontSize: "11px", marginTop: "3px", fontStyle: "italic", color: "#b45309", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span className="cg-spinner" /> Validating…
                    </div>
                  ))
                )}
              </div>

              <button
                className="chat-options-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(dropdownOpen === chat.id ? null : chat.id);
                  setRenameInput("");
                }}
              >
                ⋮
              </button>
            </div>

            {dropdownOpen === chat.id && (
              <div className="chat-dropdown">
                <button
                  onClick={async () => {
                    const newPinned = chat.pinned === "Y" ? "N" : "Y";
                    await axios.post(`${API_BASE}/pin-chat`, { chatId: chat.id, pinned: newPinned });
                    await fetchAllChats();
                    setDropdownOpen(null);
                  }}
                >
                  {chat.pinned === "Y" ? "📍 Unpin" : "📌 Pin"}
                </button>
                
                {!isByop(chat) && (
                <RequirePermission permission="executeBuilds">
                {chat.validated_final === "Y" ? (
                  <button
                    onClick={() => {
                      handleChatSelection(chat.id, "LAUNCH");
                      setDropdownOpen(null);
                    }}
                  >
                    🚀 Launch
                  </button>
                ) : (
                  <button
                    disabled
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                    title={
                      chat.validation_status === "failed"
                        ? "Validation failed — this workflow cannot be launched."
                        : "Workflow is still under validation and cannot be launched yet."
                    }
                  >
                    🚀 Launch (unavailable)
                  </button>
                )}
                </RequirePermission>
                )}

                {isByop(chat) && (
                <RequirePermission permission="executeBuilds">
                {chat.validated_final === "Y" ? (
                  <button
                    onClick={() => {
                      handleChatSelection(chat.id);
                      setDropdownOpen(null);
                    }}
                  >
                    🚀 Launch
                  </button>
                ) : (
                  <button
                    disabled
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                    title={
                      chat.validation_status === "failed"
                        ? "Docker validation failed — this pipeline cannot be launched."
                        : "Pipeline container is still being validated and cannot be launched yet."
                    }
                  >
                    🚀 Launch (unavailable)
                  </button>
                )}
                </RequirePermission>
                )}

                {isByop(chat) && (
                <RequirePermission permission="modifyBuilds">
                <button
                  onClick={() => {
                    handleChatSelection(chat.id, "BYOP_EDIT");
                    setDropdownOpen(null);
                  }}
                >
                  🛠️ Edit
                </button>
                </RequirePermission>
                )}

                <button onClick={() => setRenameInput(chat.short_name || "")}>✏️ Rename</button>

                {renameInput !== "" && (
                  <div className="rename-input-container">
                    <input
                      type="text"
                      value={renameInput}
                      onChange={(e) => setRenameInput(e.target.value)}
                    />
                    <button onClick={() => handleRename(chat.id)}>Save</button>
                  </div>
                )}

                {!isByop(chat) && (
                <RequirePermission permission="modifyBuilds">
                <button
                  onClick={() => {
                    handleChatSelection(chat.id, "MODIFY");
                    setDropdownOpen(null);
                  }}
                >
                  🛠️ Edit
                </button>
                </RequirePermission>
                )}

                <button onClick={() => handleDelete(chat.id)}>🗑️ Delete</button>
                <button onClick={() => handleShare(chat.id)}>🔗 Share</button>
              </div>
            )}
          </div>
        ))
      ) : (
        <p>No chats available.</p>
      )}
      </div>

      {totalPages > 1 && (
        <div className="chat-pagination">
          <button
            className="page-btn page-arrow"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            ‹
          </button>
          {getPageWindow(currentPage, totalPages).map((p, i) =>
            p === "..." ? (
              <span key={`gap-${i}`} className="page-ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`page-btn ${p === currentPage ? "active" : ""}`}
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            className="page-btn page-arrow"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>

    <div className="allchats-main">
      {selectedChatId && isByop(selectedChat) ? (
        viewMode === "BYOP_EDIT" ? (
          <BYOPEdit chatId={selectedChatId} />
        ) : (
          <BYOPWorkflow
            chatId={selectedChatId}
            cognitoUserId={cognitoUserId}
            organization={organization}
          />
        )
      ) : selectedChatId && (
        <div>
          {selectedChat && !selectedValidated && workflowStatus !== "INCOMPLETE" && (
            <div
              style={{
                margin: "12px 0",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid",
                borderColor:
                  selectedChat.validation_status === "failed" ? "#fca5a5" : "#fcd34d",
                background:
                  selectedChat.validation_status === "failed" ? "#fef2f2" : "#fffbeb",
                color:
                  selectedChat.validation_status === "failed" ? "#b91c1c" : "#92400e",
                fontSize: "14px",
              }}
            >
              {selectedChat.validation_status === "failed"
                ? "⚠️ This workflow failed automated validation and cannot be launched. Please edit it and resubmit."
                : "⏳ This workflow is being validated (preview run, link checks, and container build). Launching will be enabled once validation completes."}
            </div>
          )}
          {workflowStatus === "INCOMPLETE" ? (
            <div>
              <h3>Chat History</h3>
              {conversation.map((msg, index) => (
                <p key={index}>
                  <strong>{msg.role}:</strong> {msg.content}
                </p>
              ))}
            </div>
          ) : viewMode === "MODIFY" && workflowMetadataId ? (
            <ModifyWorkflow
              chatId={selectedChatId}
              workflowId={workflowMetadataId}
              workflowMetadataId={workflowMetadataId}
              cognitoUserId={cognitoUserId}
              organization={organization}
            />
          ) : viewMode === "LAUNCH" && workflowMetadataId && selectedValidated ? (
            <LaunchBuild
              chatId={selectedChatId}
              workflowId={workflowMetadataId}
              workflowMetadataId={workflowMetadataId}
              cognitoUserId={cognitoUserId}
              organization={organization}
            />
          ) : workflowStatus === "COMPLETE" && workflowMetadataId ? (
            <WorkflowDetails
              workflowId={workflowId}
              chatId={selectedChatId}
              cognitoUserId={cognitoUserId}
              workflowMetadataId={workflowMetadataId}
              organization={organization}
              launchable={selectedValidated}
            />
          ) : null}
        </div>
      )}
    </div>

    {shareModalOpen && (
      <div className="modal-overlay">
        <div className="modal-content">

          {shareStep === "select" && (
            <>
              <h3>Select the user you want to share the workflow with</h3>

              <select
                className="share-user-select"
                value={shareTargetUser || ""}
                onChange={(e) => setShareTargetUser(e.target.value)}
              >
                <option value="">-- Select User --</option>
                {orgUsers.map((u) => (
                  <option key={u.cognito_user_id} value={u.cognito_user_id}>
                    {u.name}
                  </option>
                ))}
              </select>

              <div className="share-modal-buttons">
                <button
                  disabled={!shareTargetUser}
                  onClick={() => setShareStep("confirm")}
                  className="btn-primary"
                >
                  Share
                </button>

                <button onClick={() => setShareModalOpen(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </>
          )}

          {shareStep === "confirm" && (
            <>
              <h3>Confirm Share</h3>
              <p>
                You are about to share this workflow with:
                <strong>
                  {" "}
                  {orgUsers.find((u) => u.cognito_user_id === shareTargetUser)?.name}
                </strong>
              </p>

              <button onClick={handleConfirmShare} className="btn-approve">
                Confirm
              </button>

              <button onClick={() => setShareStep("select")} className="btn-cancel">
                Back
              </button>
            </>
          )}

          {shareStep === "success" && (
            <>
              <h3>Workflow Shared Successfully</h3>
              <button onClick={() => setShareModalOpen(false)} className="primary-btn">
                Close
              </button>
            </>
          )}

        </div>
      </div>
    )}
  </div>
);
};

export default AllChats;

